interface SentryLikeEvent {
  exception?: {
    values?: Array<{
      type?: string;
      value?: string;
    }>;
  };
  tags?: Record<string, unknown>;
  user?: {
    ip_address?: string | null;
    [key: string]: unknown;
  };
  contexts?: Record<string, unknown> & {
    browser?: {
      name: string;
      version: string;
    };
  };
}

/**
 * Errors we suppress at the Sentry boundary. Matched against both the
 * exception `type` (e.g. `UnknownEnvError`) and `value` (the message),
 * because Sentry's serialization differs between throw sites:
 *   - Some SDK errors are thrown with the type name as the message, so
 *     they show up as { value: 'LaunchParamsRetrieveError' }.
 *   - Others (UnknownEnvError) carry a generic message like "No error
 *     message" but the constructor name lands in `type`.
 *
 * Each entry here MUST be already caught + handled in app code — these
 * are entries on a known-noise allowlist, not silent-fail bug coverups.
 *
 * - `LaunchParamsRetrieveError`: thrown by @telegram-apps/sdk when the
 *   app is opened outside Telegram. Surfaced to the user via the root
 *   error boundary's getErrorMessage fallback ("Откройте приложение в
 *   Telegram, или обновите версию"). Nothing to fix in code.
 * - `UnknownEnvError`: thrown deep inside @telegram-apps/sdk-react when
 *   none of the host transports (TelegramWebviewProxy.postEvent /
 *   window.external.notify / window.parent.postMessage) is reachable.
 *   Each per-call `safe()` wrapper in __root.tsx catches it locally and
 *   the React tree still renders, but every SDK init step (init,
 *   miniApp.mount, viewport.mount, swipeBehavior.mount, …) hits the same
 *   throw site, producing 6 redundant Sentry issues per affected page
 *   load. The app degrades to "no TMA host integration" — the only
 *   remediation is for the user to open the link inside Telegram, which
 *   isn't something we can fix from the page itself.
 */
export const SUPPRESSED_EXCEPTIONS = new Set([
  'LaunchParamsRetrieveError',
  'UnknownEnvError',
]);

/**
 * Noise matched on the MESSAGE rather than the exception type.
 *
 * `FunctionNotAvailableError` deliberately is NOT in SUPPRESSED_EXCEPTIONS:
 * @telegram-apps/sdk raises that one type for several distinct reasons, and
 * only one of them is environmental noise.
 *   - "…it can't be called outside Mini Apps" — the app was opened in a
 *     plain browser. Same unfixable condition as UnknownEnvError, and one
 *     affected page load emits four near-identical issues
 *     (miniApp / viewport / backButton / closingBehavior .mount).
 *   - "…is unsupported in Mini Apps version X" — an OLD Telegram client
 *     missing a feature we rely on. That IS actionable, and the `safe()`
 *     helper in __root.tsx exists precisely to capture it. Suppressing by
 *     type would silently discard it.
 *
 * postEvent bridge errors ("Java bridge method invocation error", "Java
 * object is gone") fire when the Android WebView is torn down mid-call —
 * the user closed the app. Axios timeouts are connectivity, same as below.
 */
const SUPPRESSED_MESSAGE_PATTERNS: RegExp[] = [
  /it can't be called outside Mini Apps/,
  /^Error invoking postEvent/,
  /^timeout of \d+ms exceeded$/,
  /^timeout exceeded$/,
];

/**
 * Connectivity failures are SAMPLED, not dropped.
 *
 * These are mostly flaky mobile networks, and at full volume they were the
 * second-largest consumer of the error quota. But they are also the only
 * client-side detector we have for "a subset of users cannot reach the
 * origin at all" — the Prometheus probes run inside the VPC and stay green
 * while that is happening. Dropping them outright would blind us to exactly
 * the failure mode the app.kiss-date.ru launcher exists to work around.
 *
 * Keeping a small sample preserves the trend line (a real outage moves it
 * by orders of magnitude, which survives sampling) at ~3% of the cost.
 * Sampled-through events are tagged so the rate can be corrected for.
 *
 * Matched EXACTLY, never by prefix: "Failed to fetch dynamically imported
 * module" means a stale chunk after a deploy and must always be reported.
 */
const CONNECTIVITY_MESSAGES = new Set([
  'Network Error',
  'Failed to fetch',
  'Load failed',
]);
const CONNECTIVITY_SAMPLE_RATE = 0.03;

/**
 * Backstop against runaway repeats.
 *
 * The primary fix for the known loop is that boundary reporting happens in
 * the router's defaultOnCatch (main.tsx) — once per caught error, from
 * componentDidCatch. This is here for loops we did NOT anticipate: a single
 * tab can otherwise emit the same error hundreds of times and spend the
 * whole monthly allowance.
 *
 * Three things this deliberately gets right:
 *  - The signature covers the WHOLE exception chain, not just the outermost
 *    error, so an error distinguished only by its `cause` still gets its
 *    own bucket and cannot hide the fourth different bug in a session.
 *  - The counter is time-WINDOWED. `createBeforeSend` is called once from
 *    logger.init() at module scope, and this is an SPA inside a Telegram
 *    mini app, so the closure can live for hours. Without a window, a
 *    signature would go permanently invisible after three occurrences.
 *  - The map is bounded, so an error whose message interpolates an id or a
 *    URL cannot grow it without limit (such an error also evades the cap,
 *    which the window bound at least keeps cheap).
 */
const MAX_EVENTS_PER_SIGNATURE = 3;
const SIGNATURE_WINDOW_MS = 5 * 60 * 1000;
const MAX_TRACKED_SIGNATURES = 100;

export const createBeforeSend = (
  userAgent: string,
  random: () => number = Math.random,
  now: () => number = Date.now,
) => {
  const seen = new Map<string, { count: number; windowStart: number }>();

  return <T extends SentryLikeEvent>(event: T): T | null => {
    // Sentry's linkedErrors integration PREPENDS each `cause`, so
    // values[0] is the innermost cause and the LAST entry is the error
    // that was actually thrown (see @sentry/core aggregate-errors).
    // Reading values[0] would suppress a reported error because of what
    // merely caused it — e.g. "Ошибка загрузки файла" caused by a
    // "Network Error" would vanish entirely.
    const values = event.exception?.values;
    const exception = values?.[values.length - 1];

    if (exception) {
      const exceptionType = exception.type ?? '';
      const exceptionValue = exception.value ?? '';

      // Events from the root error boundary (tagged by defaultOnCatch in
      // main.tsx) are FATAL — the user got a white screen. The message
      // patterns and the connectivity sampler below were built for HANDLED
      // noise, and with the real error now outermost they would otherwise
      // eat exactly the commonest crash classes (a loader timeout, a
      // "Network Error" that broke the render). Fatal events bypass both.
      //
      // SUPPRESSED_EXCEPTIONS still applies even to fatal events: opening
      // the app outside Telegram crashes the boundary BY DESIGN on every
      // such visit, and no code change can fix it — reporting it would
      // just refill the quota with environment noise. The per-signature
      // cap below also still applies — fatal or not, a crash loop must
      // not spend the monthly allowance.
      const isFatalBoundaryEvent = event.tags?.section === 'error-boundary';

      if (
        SUPPRESSED_EXCEPTIONS.has(exceptionType) ||
        SUPPRESSED_EXCEPTIONS.has(exceptionValue)
      ) {
        return null;
      }

      if (
        !isFatalBoundaryEvent &&
        SUPPRESSED_MESSAGE_PATTERNS.some((p) => p.test(exceptionValue))
      ) {
        return null;
      }

      if (!isFatalBoundaryEvent && CONNECTIVITY_MESSAGES.has(exceptionValue)) {
        if (random() >= CONNECTIVITY_SAMPLE_RATE) {
          return null;
        }
        event.tags = {
          ...event.tags,
          sampled: `connectivity@${CONNECTIVITY_SAMPLE_RATE}`,
        };
      }

      const signature = (values ?? [])
        .map((v) => `${v.type ?? ''}|${v.value ?? ''}`)
        .join('>');
      const entry = seen.get(signature);
      const timestamp = now();

      if (!entry || timestamp - entry.windowStart >= SIGNATURE_WINDOW_MS) {
        if (!entry && seen.size >= MAX_TRACKED_SIGNATURES) {
          seen.clear();
        }
        seen.set(signature, { count: 1, windowStart: timestamp });
      } else if (entry.count >= MAX_EVENTS_PER_SIGNATURE) {
        return null;
      } else {
        entry.count += 1;
      }
    }

    if (event.user) {
      event.user.ip_address = undefined;
    }

    if (event.contexts) {
      event.contexts.browser = {
        name: userAgent,
        version: userAgent,
      };
    }

    return event;
  };
};
